import React, { useState, useEffect, useMemo } from 'react';
import { Card, Button, Select, Form, Input, Space, Typography, message, Spin, Alert, Divider, Progress, Tag, Timeline, Tabs, Statistic, Row, Col, Modal, List, Collapse, Badge } from 'antd';
import { PlayCircle, Rocket, CheckCircle, Clock, AlertCircle, Eye, History, Loader2, Bot, Wrench, Plug, Copy, BarChart3, Pencil, Save, X, MapPin } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useCrews } from '../hooks/useCrews';
import { apiService } from '../services/api';
import { CrewRun, RunStep, CrewKickoffPrefillResponse, LocationKeyword } from '../types';
import { useTenantStore } from '../stores/useTenantStore';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

// Helper function to group array by key
const groupBy = <T,>(array: T[], key: keyof T): Record<string, T[]> => {
  return array.reduce((result, item) => {
    const groupKey = String(item[key]);
    if (!result[groupKey]) {
      result[groupKey] = [];
    }
    result[groupKey].push(item);
    return result;
  }, {} as Record<string, T[]>);
};

const RunPage: React.FC = () => {
  const navigate = useNavigate();
  const { data: crews = [], isLoading: crewsLoading } = useCrews();
  const { currentTenant } = useTenantStore();
  const [selectedCrew, setSelectedCrew] = useState<string | null>(null);
  const [running, setRunning] = useState(false);
  const [currentRun, setCurrentRun] = useState<CrewRun | null>(null);
  const [prefillData, setPrefillData] = useState<CrewKickoffPrefillResponse | null>(null);
  const [locationKeywords, setLocationKeywords] = useState<LocationKeyword[]>([]);
  const [selectedKeyword, setSelectedKeyword] = useState<string | null>(null);
  const [prefillLoading, setPrefillLoading] = useState(false);
  const [recentRuns, setRecentRuns] = useState<CrewRun[]>([]);
  const [form] = Form.useForm();
  const [configForm] = Form.useForm();
  const serviceLocations = Form.useWatch('service_locations', configForm) || [];
  const disallowedLocations = Form.useWatch('disallowed_locations', configForm) || [];
  const [configModified, setConfigModified] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingField, setEditingField] = useState<string | null>(null);
  const [locationSearchTerm, setLocationSearchTerm] = useState('');

  const filteredServiceLocations = useMemo(() => {
    if (!locationSearchTerm) return serviceLocations;
    const normalized = locationSearchTerm.toLowerCase();
    return serviceLocations.filter((loc: string) => loc.toLowerCase().includes(normalized));
  }, [locationSearchTerm, serviceLocations]);

  // Generate location suggestions from existing locations and keywords
  const locationSuggestions = useMemo(() => {
    const suggestions = new Set<string>();
    
    // Add existing service locations from brand bible (prefer locations JSONB, fallback to service_locations)
    const brandBibleLocations = prefillData?.brand_bible?.locations
      ? Object.keys(prefillData.brand_bible.locations)
      : (prefillData?.brand_bible?.service_locations ?? []);
    brandBibleLocations.forEach((loc: string) => suggestions.add(loc));
    
    // Extract locations from location keywords
    if (locationKeywords.length > 0) {
      locationKeywords.forEach(kw => {
        // Extract location, state from keywords like "plumber in St Louis MO" or "plumber near Austin TX"
        const keyword = kw.keyword.toLowerCase();
        const nearMatch = keyword.match(/(?:near|in)\s+([a-z\s]+?)\s+([a-z]{2})/i);
        if (nearMatch) {
          const city = nearMatch[1].trim().split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
          const state = nearMatch[2].toUpperCase();
          suggestions.add(`${city}, ${state}`);
        }
      });
    }
    
    return Array.from(suggestions).sort();
  }, [prefillData, locationKeywords]);

  // Generate service suggestions from existing services
  const serviceSuggestions = useMemo(() => {
    const suggestions = new Set<string>();
    
    // Add existing services offered from brand bible
    if (prefillData?.brand_bible?.services_offered) {
      if (Array.isArray(prefillData.brand_bible.services_offered)) {
        prefillData.brand_bible.services_offered.forEach((service: string) => suggestions.add(service));
      } else if (typeof prefillData.brand_bible.services_offered === 'string') {
        // Handle case where services_offered might be a comma-separated string
        prefillData.brand_bible.services_offered.split(',').forEach((service: string) => {
          const trimmed = service.trim();
          if (trimmed) suggestions.add(trimmed);
        });
      }
    }
    
    return Array.from(suggestions).sort();
  }, [prefillData]);

  useEffect(() => {
    const fetchRuns = async () => {
      try {
        const runs = await apiService.getCrewRuns();
        setRecentRuns(runs.sort((a, b) =>
          new Date(b.started_at || 0).getTime() - new Date(a.started_at || 0).getTime()
        ).slice(0, 5));
      } catch (e) {
        console.error('Failed to fetch recent runs', e);
      }
    };
    fetchRuns();
  }, [currentRun]); // Refresh when currentRun updates

  // Auto-select "Location Page Creation" crew or first available crew
  useEffect(() => {
    if (crews.length > 0 && !selectedCrew) {
      const locationCrew = crews.find(c => c.name.toLowerCase().includes('location'));
      setSelectedCrew(locationCrew?.id || crews[0].id);
    }
  }, [crews, selectedCrew]);

  const handleCopyOutput = async () => {
    if (!currentRun?.output) return;
    try {
      await navigator.clipboard.writeText(currentRun.output);
      message.success('Execution output copied to clipboard');
    } catch (err) {
      console.error('Failed to copy run output', err);
      message.error('Unable to copy execution output');
    }
  };

  const handleClearRun = () => {
    setCurrentRun(null);
    setRunning(false);
    message.info('Execution cleared');
  };

  // Poll for run status updates
  useEffect(() => {
    if (!currentRun || currentRun.status === 'completed' || currentRun.status === 'failed') {
      return;
    }

    const pollInterval = setInterval(async () => {
      try {
        const updatedRun = await apiService.getCrewRun(currentRun.id);
        setCurrentRun(updatedRun);

        if (updatedRun.status === 'completed') {
          message.success('Crew execution completed!');
          setRunning(false);
        } else if (updatedRun.status === 'failed') {
          message.error(`Crew execution failed: ${updatedRun.error || 'Unknown error'}`);
          setRunning(false);
        }
      } catch (error) {
        console.error('Error polling run status:', error);
      }
    }, 3000); // Poll every 3 seconds

    return () => clearInterval(pollInterval);
  }, [currentRun]);

  useEffect(() => {
    if (!editModalVisible) {
      setLocationSearchTerm('');
    }
  }, [editModalVisible]);

  useEffect(() => {
    const fetchPrefillData = async () => {
      if (!selectedCrew || !currentTenant) {
        setPrefillData(null);
        setLocationKeywords([]);
        return;
      }

      setPrefillLoading(true);
      try {
        const data = await apiService.getKickoffPrefill(selectedCrew, currentTenant);
        setPrefillData(data);
        setLocationKeywords(data.location_keywords || []);

        // Initialize config form with prefill data
        // Default to first 2 service locations if available (prefer locations JSONB)
        const serviceLocations = data.brand_bible?.locations
          ? Object.keys(data.brand_bible.locations)
          : (data.brand_bible?.service_locations ?? []);
        const defaultServiceLocations = serviceLocations.length > 0
          ? serviceLocations.slice(0, 2)
          : [];

        // Default to first 2 services offered if available
        let servicesOffered = data.brand_bible?.services_offered || [];
        if (typeof servicesOffered === 'string') {
          servicesOffered = servicesOffered.split(',').map((s: string) => s.trim()).filter(Boolean);
        }
        const defaultServicesOffered = servicesOffered.length > 0
          ? servicesOffered.slice(0, 2)
          : [];
        
        configForm.setFieldsValue({
          business_name: data.brand_bible?.business_name,
          tone_of_writing: data.brand_bible?.tone_of_writing,
          ideal_client: data.brand_bible?.ideal_client,
          service_locations: defaultServiceLocations,
          services_offered: defaultServicesOffered,
          disallowed_locations: (data.tenant_settings?.disallowed_locations || '')
            .split(',')
            .map((loc: string) => loc.trim())
            .filter(Boolean),
          max_words: data.tenant_settings?.max_words,
          min_words: data.tenant_settings?.min_words,
          sections: data.tenant_settings?.sections,
        });
        setConfigModified(false);

        message.success('Loaded tenant configuration and keywords');

      } catch (error) {
        console.error('Failed to fetch prefill data:', error);
        // Show specific error to help debugging
        if (error instanceof Error) {
            message.error(`Failed to load config: ${error.message}`);
        }
      } finally {
        setPrefillLoading(false);
      }
    };

    fetchPrefillData();
  }, [selectedCrew, currentTenant, form]);

  const handleKeywordSelect = (keyword: string) => {
    setSelectedKeyword(keyword);
    
    // Update the inputs JSON with the selected keyword
    const currentInputsStr = form.getFieldValue('inputs');
    try {
      let currentInputs = currentInputsStr ? JSON.parse(currentInputsStr) : {};
      
      // Find full keyword object for context if needed
      const keywordObj = locationKeywords.find(k => k.keyword === keyword);
      
      currentInputs = {
        ...currentInputs,
        keyword: keyword,
        topic: keyword,
      };

      if (keywordObj?.cluster_name) {
        currentInputs.cluster_name = keywordObj.cluster_name;
      }

      form.setFieldsValue({
        inputs: JSON.stringify(currentInputs, null, 2)
      });
    } catch (e) {
      console.error('Error updating inputs with keyword', e);
    }
  };

  const handleCrewSelect = (crewId: string) => {
    setSelectedCrew(crewId);
    form.resetFields(['inputs']);
  };

  const handleRun = async (values: any) => {
    if (!selectedCrew) {
      message.warning('Please select a crew first');
      return;
    }

    // Ensure the run only executes for explicitly selected locations and services
    const configValues = configForm.getFieldsValue();
    const selectedServiceLocations: string[] = configValues.service_locations || [];
    const selectedServicesOffered: string[] = configValues.services_offered || [];

    if (selectedServiceLocations.length === 0) {
      message.warning('Please select at least one service location in the configuration');
      return;
    }

    if (selectedServicesOffered.length === 0) {
      message.warning('Please select at least one service in the configuration');
      return;
    }

    setRunning(true);
    setCurrentRun(null);
    try {
      let inputs = {};

      // Remove the location/service arrays from the brand bible so we can rely solely
      // on the explicitly chosen values from the Run page configuration
      const brandBibleRest = { ...(prefillData?.brand_bible || {}) };
      delete brandBibleRest.service_locations;
      delete brandBibleRest.services_offered;

      // Merge: Base inputs from form + Modified config + Brand Bible + Tenant Settings
      const mergedInputs = {
        // Add required fields for crew execution
        tenant_id: currentTenant,
        website: prefillData?.website || currentTenant,
        // Note: keyword is NOT included - it will be auto-generated by the crew's keyword research tasks
        fal_model: 'fal-ai/flux/schnell',
        // Add all brand bible data
        ...brandBibleRest,
        // Add all tenant settings
        ...(prefillData?.tenant_settings || {}),
        // Override with modified config values
        ...(configValues.business_name && { business_name: configValues.business_name }),
        ...(configValues.tone_of_writing && { tone_of_writing: configValues.tone_of_writing }),
        ...(configValues.ideal_client && { ideal_client: configValues.ideal_client }),
        service_locations: selectedServiceLocations,
        services_offered: selectedServicesOffered,
        ...(configValues.disallowed_locations && {
          disallowed_locations: Array.isArray(configValues.disallowed_locations)
            ? configValues.disallowed_locations.join(', ')
            : configValues.disallowed_locations,
        }),
        ...(configValues.max_words && { max_words: configValues.max_words }),
        ...(configValues.min_words && { min_words: configValues.min_words }),
        ...(configValues.sections && { sections: configValues.sections }),
      };

      // Parse and merge additional JSON inputs from the textarea
      if (values.inputs) {
        try {
          const additionalInputs = JSON.parse(values.inputs);
          Object.assign(mergedInputs, additionalInputs);
        } catch (e) {
          message.error('Invalid JSON in additional inputs field');
          setRunning(false);
          return;
        }
      }

      inputs = mergedInputs;

      const run = await apiService.kickoffCrew(selectedCrew, inputs);
      setCurrentRun(run);
      message.success('Crew execution started with your customized configuration!');
    } catch (error) {
      message.error('Failed to start crew execution');
      console.error(error);
      setRunning(false);
    }
  };

  const getRunTitle = (run: CrewRun): string => {
    // Priority 1: Crew name (most important!)
    const crew = crews.find(c => c.id === run.crew_id);
    const crewName = crew?.name || 'Unknown Crew';

    // Priority 2: Add context from inputs if available
    if (run.inputs) {
      const inputs = run.inputs;

      // Location-specific run
      if (inputs.location_name) {
        return `${crewName} - ${inputs.location_name}`;
      }

      // Keyword-specific run
      if (inputs.keyword) {
        return `${crewName} - ${inputs.keyword}`;
      }

      // Topic-specific run
      if (inputs.topic) {
        return `${crewName} - ${inputs.topic}`;
      }

      // Bulk location generation
      if (Array.isArray(inputs.service_locations) && inputs.service_locations.length > 0) {
        return `${crewName} (${inputs.service_locations.length} locations)`;
      }
    }

    // Just return crew name if no additional context
    return crewName;
  };

  const getStatusTag = (status: CrewRun['status']) => {
    const statusConfig = {
      completed: { color: 'success', icon: <CheckCircleOutlined />, text: 'Completed' },
      running: { color: 'processing', icon: <ClockCircleOutlined />, text: 'Running' },
      failed: { color: 'error', icon: <ExclamationCircleOutlined />, text: 'Failed' },
      pending: { color: 'default', icon: <ClockCircleOutlined />, text: 'Pending' },
    };

    const config = statusConfig[status];
    return (
      <Tag icon={config.icon} color={config.color}>
        {config.text}
      </Tag>
    );
  };

  const getProgressPercent = (run: CrewRun) => {
    // Use actual progress if available from backend
    if (run.progress !== undefined && run.progress !== null) {
      return run.progress;
    }

    // Fallback to status-based estimation
    switch (run.status) {
      case 'pending': return 10;
      case 'running': return 50;
      case 'completed': return 100;
      case 'failed': return 50;
      default: return 0;
    }
  };

  const getElapsedTime = (startedAt?: string) => {
    if (!startedAt) return 'N/A';
    const start = new Date(startedAt).getTime();
    const now = Date.now();
    const elapsed = Math.floor((now - start) / 1000);

    if (elapsed < 60) return `${elapsed}s`;
    if (elapsed < 3600) return `${Math.floor(elapsed / 60)}m ${elapsed % 60}s`;
    return `${Math.floor(elapsed / 3600)}h ${Math.floor((elapsed % 3600) / 60)}m`;
  };

  const getCrewDetails = () => {
    if (!selectedCrew) return null;
    const crew = crews.find(c => c.id === selectedCrew);
    return crew;
  };

  const getStepIcon = (step: RunStep) => {
    if (step.status === 'running') return <LoadingOutlined />;
    if (step.status === 'failed') return <ExclamationCircleOutlined />;
    if (step.status === 'completed') return <CheckCircleOutlined />;

    switch (step.step_type) {
      case 'agent': return <RobotOutlined />;
      case 'tool': return <ToolOutlined />;
      case 'llm-call': return <ApiOutlined />;
      default: return <ClockCircleOutlined />;
    }
  };

  const getStepStatusColor = (status: RunStep['status']) => {
    switch (status) {
      case 'completed': return 'green';
      case 'running': return 'blue';
      case 'failed': return 'red';
      default: return 'gray';
    }
  };

  const formatStepDuration = (durationMs?: number) => {
    if (!durationMs) return 'N/A';
    const seconds = durationMs / 1000;
    if (seconds < 60) return `${seconds.toFixed(1)}s`;
    return `${Math.floor(seconds / 60)}m ${(seconds % 60).toFixed(0)}s`;
  };

  const getExecutionDurationSeconds = () => {
    if (!currentRun?.started_at) return null;
    const startMs = new Date(currentRun.started_at).getTime();
    if (currentRun.completed_at) {
      return Math.max(0, Math.round((new Date(currentRun.completed_at).getTime() - startMs) / 1000));
    }
    return Math.max(0, Math.round((Date.now() - startMs) / 1000));
  };

  const formatDurationLabel = (seconds?: number | null) => {
    if (!seconds && seconds !== 0) return 'N/A';
    if (seconds < 60) return `${seconds}s`;
    if (seconds < 3600) {
      const mins = Math.floor(seconds / 60);
      const rem = seconds % 60;
      return `${mins}m ${rem}s`;
    }
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${mins}m`;
  };

  const getMockSteps = (run: CrewRun): RunStep[] => {
    const baseSteps: Omit<RunStep, 'id'>[] = [
      {
        step_type: 'task',
        name: 'Generate Image Prompts',
        agent_name: 'Image Prompt Agent',
        status: run.status === 'pending' ? 'pending' : 'completed',
        started_at: run.started_at,
        summary: 'Generated AI image prompts for the location page',
      },
      {
        step_type: 'task',
        name: 'Generate Page Headings',
        agent_name: 'Headings Agent',
        status: run.status === 'pending' ? 'pending' : run.status === 'running' ? 'running' : 'completed',
        summary: 'Created SEO-optimized headings structure',
      },
      {
        step_type: 'task',
        name: 'Generate Meta Description',
        agent_name: 'Meta Description Agent',
        status: run.status === 'completed' ? 'completed' : 'pending',
        summary: 'Crafted meta description for search engines',
      },
      {
        step_type: 'task',
        name: 'Generate Content Sections',
        agent_name: 'Content Sections Agent',
        status: run.status === 'completed' ? 'completed' : 'pending',
        summary: 'Generated detailed content for each section',
      },
      {
        step_type: 'task',
        name: 'Assemble Final Page Data',
        agent_name: 'Page Assembler',
        status: run.status === 'completed' ? 'completed' : 'pending',
        summary: 'Combined all components into final page structure',
      },
    ];

    return baseSteps.map((step, idx) => ({
      ...step,
      id: `mock-step-${idx}`,
    }));
  };

  const crewDetails = getCrewDetails();
  const runSteps = useMemo(() => {
    if (!currentRun) return [];
    if (currentRun.steps && currentRun.steps.length > 0) {
      return currentRun.steps;
    }
    return getMockSteps(currentRun);
  }, [currentRun]);

  type EssentialMetric = {
    key: string;
    title: string;
    value: string | number;
    prefix?: React.ReactNode;
    suffix?: React.ReactNode;
    precision?: number;
    description?: string;
    icon?: React.ReactNode;
  };

  const essentialMetrics = useMemo<EssentialMetric[]>(() => {
    if (!currentRun) return [];

    const totalSteps = runSteps.length;
    const completedSteps = runSteps.filter((step) => step.status === 'completed').length;
    const failedSteps = runSteps.filter((step) => step.status === 'failed').length;
    const successRate = totalSteps ? Math.round((completedSteps / totalSteps) * 100) : 0;
    const totalTokens = runSteps.reduce((sum, step) => sum + (step.token_count || 0), 0);
    const totalCost = runSteps.reduce((sum, step) => sum + (step.cost || 0), 0);
    const involvedAgents = new Set<string>();
    runSteps.forEach((step) => {
      if (step.agent_name) {
        involvedAgents.add(step.agent_name);
      }
    });
    const agentCount = involvedAgents.size || crewDetails?.agents?.length || 0;
    const durationSeconds = getExecutionDurationSeconds();

    return [
      {
        key: 'steps',
        title: 'Steps Completed',
        value: totalSteps ? `${completedSteps}/${totalSteps}` : 'N/A',
        description: failedSteps ? `${failedSteps} steps failed` : 'All steps healthy',
      },
      {
        key: 'success',
        title: 'Success Rate',
        value: successRate,
        suffix: '%',
        description: 'Based on completed timeline steps',
      },
      {
        key: 'agents',
        title: 'Agents Involved',
        value: agentCount || 'N/A',
        description: agentCount ? 'Unique agents participating' : 'Agent data unavailable',
      },
      {
        key: 'tokens',
        title: 'Tokens Used',
        value: totalTokens || 'N/A',
        description: totalTokens ? 'Aggregated from agent/tool calls' : 'Token metrics not reported',
      },
      {
        key: 'cost',
        title: 'Estimated Cost',
        value: totalCost || 0,
        prefix: '$',
        precision: totalCost > 0 && totalCost < 1 ? 4 : 2,
        description: totalCost ? 'Summed from per-step costs' : 'Cost metrics not reported',
      },
      {
        key: 'duration',
        title: 'Execution Time',
        value: formatDurationLabel(durationSeconds),
        icon: <ClockCircleOutlined />,
        description: currentRun.completed_at ? 'Total runtime' : 'Updating while run is active',
      },
    ];
  }, [currentRun, runSteps, crewDetails]);

  const logs = useMemo(() => {
    if (!currentRun) return [];
    const entries: string[] = [];
    if (currentRun.progress_message) {
      entries.push(`[Run] ${currentRun.progress_message}`);
    }
    runSteps.forEach((step) => {
      if (step.log) {
        entries.push(`[${step.name}] ${step.log}`);
      } else if (step.summary) {
        entries.push(`[${step.name}] ${step.summary}`);
      }
    });
    if (currentRun.error) {
      entries.push(`[Error] ${currentRun.error}`);
    }
    if (entries.length === 0 && currentRun.status === 'completed' && currentRun.output) {
      entries.push('[Run] Output generated successfully.');
    }
    return entries;
  }, [currentRun, runSteps]);

  const timelineItems = useMemo(() => runSteps.map((step) => ({
    color: getStepStatusColor(step.status),
    dot: getStepIcon(step),
    children: (
      <div>
        <Space direction="vertical" size={2} style={{ width: '100%' }}>
          <Space>
            <Text strong>{step.name}</Text>
            <Tag>{step.step_type.toUpperCase()}</Tag>
          </Space>
          {step.agent_name && (
            <Text type="secondary">Agent: {step.agent_name}</Text>
          )}
          {step.summary && (
            <Text>{step.summary}</Text>
          )}
          <Space size="middle">
            <Text type="secondary">Status: {step.status}</Text>
            {step.duration_ms && (
              <Text type="secondary">Duration: {formatStepDuration(step.duration_ms)}</Text>
            )}
          </Space>
        </Space>
      </div>
    ),
  })), [runSteps]);

  // Helper to organize steps by agent for hierarchical display
  const organizeStepsByAgent = (steps: RunStep[] = []) => {
    const agentMap = new Map<string, RunStep[]>();

    steps.forEach(step => {
      const agentName = step.agent_name || 'Unknown Agent';
      if (!agentMap.has(agentName)) {
        agentMap.set(agentName, []);
      }
      agentMap.get(agentName)!.push(step);
    });

    return agentMap;
  };

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Title level={2}>
          <RocketOutlined /> Run Crew
        </Title>
        <Paragraph type="secondary">
          Select a crew and provide inputs to start execution
        </Paragraph>
      </div>

      <Row gutter={24}>
        <Col xs={24} lg={12}>
          <Card>
            <Form form={form} layout="vertical" onFinish={handleRun}>
            <Form.Item
              label="Select Crew"
            >
              <Select
                placeholder="Choose a crew to run"
                size="large"
                value={selectedCrew}
                onChange={handleCrewSelect}
                disabled={running}
                loading={crewsLoading}
              >
                {crews.map((crew) => (
                  <Select.Option key={crew.id} value={crew.id}>
                    <Space direction="vertical" size={0}>
                      <Text strong>{crew.name}</Text>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {crew.agents?.length || 0} agents, {crew.tasks?.length || 0} tasks
                      </Text>
                    </Space>
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            {crewDetails && (
              <>
                <Divider />
                <div style={{ marginBottom: 24, padding: 16, backgroundColor: '#f5f5f5', borderRadius: 4 }}>
                  <Title level={5}>Selected Crew Configuration</Title>
                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    <div>
                      <Text strong>Description: </Text>
                      <Text>{crewDetails.description || 'No description'}</Text>
                    </div>
                    <div>
                      <Text strong>Process: </Text>
                      <Text>{crewDetails.process || 'sequential'}</Text>
                    </div>
                    <div>
                      <Text strong>Agents: </Text>
                      <Text>{crewDetails.agents?.map(a => a.name).join(', ') || 'None'}</Text>
                    </div>
                    <div>
                      <Text strong>Tasks: </Text>
                      <Text>{crewDetails.tasks?.map(t => t.name).join(', ') || 'None'}</Text>
                    </div>
                  </Space>
                </div>
                <Divider />
              </>
            )}

            {/* Keyword selection removed - keywords are now auto-generated by the crew's Ahrefs-powered keyword research tasks */}

            <Form.Item
              name="inputs"
              label="Additional Input Parameters (JSON)"
              extra="Optional workflow-specific inputs. Brand Bible and workspace settings are loaded automatically from configuration."
            >
              <TextArea
                rows={4}
                placeholder={'{\n  "keyword_override": "custom keyword",\n  "custom_field": "value"\n}'}
                style={{ fontFamily: 'monospace', fontSize: '12px' }}
                disabled={running}
              />
            </Form.Item>

            {!currentTenant && (
              <Alert
                message="No workspace selected"
                description="Please select a workspace/website in the global settings to load configuration."
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
              />
            )}

            {!selectedCrew && (
              <Alert
                message="No Crew Selected"
                description="Please select a crew from the dropdown above to continue"
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
              />
            )}

            {crews.length === 0 && !crewsLoading && (
              <Alert
                message="No Crews Available"
                description="You need to create at least one crew before you can run it. Go to the Crews page to create one."
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
              />
            )}

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                icon={running ? <Spin /> : <PlayCircleOutlined />}
                size="large"
                disabled={!selectedCrew || running || crews.length === 0 || !currentTenant}
                loading={running}
                block
              >
                {running ? 'Running Crew...' : 'Start Execution'}
              </Button>
            </Form.Item>
            </Form>
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          {!currentRun && (
            <Card 
              title={<Space><ToolOutlined /> Configuration & History</Space>}
              extra={
                <Button type="link" onClick={() => navigate('/results')}>View All Runs</Button>
              }
            >
              <Tabs defaultActiveKey="config" items={[
                {
                  key: 'config',
                  label: (
                    <Space>
                      Context & Config
                      {configModified && <Tag color="warning">Modified</Tag>}
                    </Space>
                  ),
                  children: (
                    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                      {!selectedCrew ? (
                        <Alert message="Select a crew to view configuration" type="info" showIcon />
                      ) : prefillLoading ? (
                        <div style={{ textAlign: 'center', padding: 20 }}><Spin /></div>
                      ) : prefillData ? (
                        <>
                          <Alert
                            type="info"
                            showIcon
                            message="Review & Edit Configuration"
                            description="Click on any field to edit before running the crew. Changes will be saved for this execution only."
                            style={{ marginBottom: 12 }}
                          />

                          <Form
                            form={configForm}
                            layout="vertical"
                            initialValues={{
                              business_name: prefillData.brand_bible?.business_name,
                              tone_of_writing: prefillData.brand_bible?.tone_of_writing,
                              ideal_client: prefillData.brand_bible?.ideal_client,
                              service_locations: prefillData.brand_bible?.locations
                                ? Object.keys(prefillData.brand_bible.locations)
                                : (prefillData.brand_bible?.service_locations ?? []),
                              services_offered: prefillData.brand_bible?.services_offered || [],
                              max_words: prefillData.tenant_settings?.max_words,
                              min_words: prefillData.tenant_settings?.min_words,
                              sections: prefillData.tenant_settings?.sections,
                            }}
                            onValuesChange={() => setConfigModified(true)}
                          >
                            {/* Hidden form items to register fields */}
                            <Form.Item name="service_locations" hidden><Input /></Form.Item>
                            <Form.Item name="services_offered" hidden><Input /></Form.Item>
                            {/* Business Info Card */}
                            <Card size="small" style={{ marginBottom: 12, background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                                <Text strong>Business Information</Text>
                                <Form.Item label="Business Name" name="business_name" style={{ marginBottom: 8 }}>
                                  <Input
                                    prefix={<EditOutlined style={{ color: '#1890ff' }} />}
                                    placeholder="Enter business name"
                                  />
                                </Form.Item>
                                <Form.Item label="Tone of Writing" name="tone_of_writing" style={{ marginBottom: 8 }}>
                                  <TextArea
                                    rows={2}
                                    placeholder="e.g., Professional, friendly, instructional"
                                  />
                                </Form.Item>
                                <Form.Item label="Target Audience" name="ideal_client" style={{ marginBottom: 0 }}>
                                  <TextArea
                                    rows={2}
                                    placeholder="Describe your ideal client/audience"
                                  />
                                </Form.Item>
                              </Space>
                            </Card>

                            {/* Service Locations Card */}
                            <Card
                              size="small"
                              style={{ marginBottom: 12, background: '#f0fdf4', border: '1px solid #dcfce7' }}
                              title={
                                <Space>
                                  <Text strong>Service Locations</Text>
                                  <Tag color="success">
                                    {(configForm.getFieldValue('service_locations') || []).length} locations
                                  </Tag>
                                </Space>
                              }
                              extra={
                                <Button
                                  type="link"
                                  size="small"
                                  icon={<EditOutlined />}
                                  onClick={() => {
                                    setEditingField('service_locations');
                                    setEditModalVisible(true);
                                  }}
                                >
                                  Edit All
                                </Button>
                              }
                            >
                          <div style={{ maxHeight: '150px', overflowY: 'auto' }}>
                            <Space size={[0, 4]} wrap>
                              {(configForm.getFieldValue('service_locations') || []).map((location: string, idx: number) => (
                                <Tag key={idx} color="green">{location}</Tag>
                              ))}
                              {(!configForm.getFieldValue('service_locations') || configForm.getFieldValue('service_locations').length === 0) && (
                                <Text type="secondary" italic>No locations defined</Text>
                              )}
                            </Space>
                          </div>
                            </Card>

                            {/* Services Offered Card */}
                            <Card
                              size="small"
                              style={{ marginBottom: 12, background: '#eff6ff', border: '1px solid #dbeafe' }}
                              title={
                                <Space>
                                  <Text strong>Services Offered</Text>
                                  <Tag color="blue">
                                    {(configForm.getFieldValue('services_offered') || []).length} services
                                  </Tag>
                                </Space>
                              }
                              extra={
                                <Button
                                  type="link"
                                  size="small"
                                  icon={<EditOutlined />}
                                  onClick={() => {
                                    setEditingField('services_offered');
                                    setEditModalVisible(true);
                                  }}
                                >
                                  Edit All
                                </Button>
                              }
                            >
                              <div style={{ maxHeight: '150px', overflowY: 'auto' }}>
                                <Space size={[0, 4]} wrap direction="vertical" style={{ width: '100%' }}>
                                  {(configForm.getFieldValue('services_offered') || []).length > 0 ? (
                                    configForm.getFieldValue('services_offered').map((service: string, idx: number) => (
                                      <Tag key={idx} color="blue">{service}</Tag>
                                    ))
                                  ) : (
                                    <Text type="secondary" italic>No services defined</Text>
                                  )}
                                </Space>
                              </div>
                            </Card>

                            {/* Content Settings Card */}
                            <Card size="small" style={{ marginBottom: 12, background: '#fff7ed', border: '1px solid #ffedd5' }}>
                              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                                <Text strong>Content Settings</Text>
                                <Row gutter={12}>
                                  <Col span={8}>
                                    <Form.Item label="Max Words" name="max_words" style={{ marginBottom: 0 }}>
                                      <Input type="number" placeholder="2000" />
                                    </Form.Item>
                                  </Col>
                                  <Col span={8}>
                                    <Form.Item label="Min Words" name="min_words" style={{ marginBottom: 0 }}>
                                      <Input type="number" placeholder="1000" />
                                    </Form.Item>
                                  </Col>
                                  <Col span={8}>
                                    <Form.Item label="Sections" name="sections" style={{ marginBottom: 0 }}>
                                      <Input type="number" placeholder="5" />
                                    </Form.Item>
                                  </Col>
                                </Row>
                              </Space>
                            </Card>

                            {configModified && (
                              <Alert
                                type="warning"
                                showIcon
                                message="You have unsaved changes"
                                description="These modifications will be applied to this crew execution only."
                                action={
                                  <Button
                                    size="small"
                                    type="primary"
                                    onClick={() => {
                                      message.success('Configuration ready for execution');
                                      setConfigModified(false);
                                    }}
                                  >
                                    Confirm Changes
                                  </Button>
                                }
                              />
                            )}
                          </Form>
                        </>
                      ) : (
                        <Alert message="No configuration data found" type="warning" />
                      )}
                    </Space>
                  )
                },
                {
                  key: 'history',
                  label: 'Recent Executions',
                  children: (
                    recentRuns.length === 0 ? (
                      <Alert 
                        message="No recent executions" 
                        description="Start a new crew run to see live status here." 
                        type="info" 
                        showIcon 
                      />
                    ) : (
                      <List
                        itemLayout="horizontal"
                        dataSource={recentRuns}
                        renderItem={(run) => (
                          <List.Item
                            actions={[
                              <Button type="link" size="small" onClick={() => setCurrentRun(run)}>View</Button>
                            ]}
                          >
                            <List.Item.Meta
                              avatar={getStatusTag(run.status)}
                              title={<Text style={{ fontSize: 13 }} strong>{getRunTitle(run)}</Text>}
                              description={
                                <Space direction="vertical" size={0}>
                                  <Text type="secondary" style={{ fontSize: 11 }}>
                                    {run.id.substring(0, 8)}... • {run.started_at ? new Date(run.started_at).toLocaleDateString() : 'N/A'}
                                  </Text>
                                </Space>
                              }
                            />
                          </List.Item>
                        )}
                      />
                    )
                  )
                }
              ]} />
            </Card>
          )}

          {currentRun && (
          <Card
            style={{ marginTop: 16 }}
            title={
              <Space>
                <Text strong>Live Execution Status</Text>
                {getStatusTag(currentRun.status)}
              </Space>
            }
            extra={
              <Space>
                <Button
                  type="link"
                  icon={<EyeOutlined />}
                  onClick={() => navigate('/results')}
                >
                  View in Results
                </Button>
                <Button
                  type="link"
                  danger
                  icon={<ClearOutlined />}
                  onClick={handleClearRun}
                >
                  Clear
                </Button>
              </Space>
            }
          >
            <Tabs
              defaultActiveKey="overview"
              items={[
                {
                  key: 'overview',
                  label: 'Overview',
                  children: (
                    <Space direction="vertical" size="large" style={{ width: '100%' }}>
                      <Row gutter={16}>
                        <Col xs={24} md={8}>
                          <Statistic title="Run ID" valueRender={() => <Text code>{currentRun.id}</Text>} />
                        </Col>
                        <Col xs={24} md={8}>
                          <Statistic title="Elapsed Time" prefix={<ClockCircleOutlined />} value={getElapsedTime(currentRun.started_at)} />
                        </Col>
                        <Col xs={24} md={8}>
                          <Statistic title="Last Updated" prefix={<HistoryOutlined />} value={currentRun.last_updated_at ? new Date(currentRun.last_updated_at).toLocaleTimeString() : 'Just now'} />
                        </Col>
                      </Row>

                      <div>
                        <Text type="secondary" style={{ marginBottom: 8, display: 'block' }}>
                          Progress
                        </Text>
                        <Progress
                          percent={getProgressPercent(currentRun)}
                          status={currentRun.status === 'failed' ? 'exception' : currentRun.status === 'completed' ? 'success' : 'active'}
                          strokeColor={
                            currentRun.status === 'completed' ? '#52c41a' :
                            currentRun.status === 'failed' ? '#ff4d4f' :
                            currentRun.status === 'running' ? '#1890ff' : '#d9d9d9'
                          }
                        />
                        <Text type="secondary">
                          {currentRun.progress_message || (currentRun.status === 'running' ? 'Agents are collaborating...' : currentRun.status)}
                        </Text>
                      </div>

                      {essentialMetrics.length > 0 && (
                        <>
                          <Divider orientation="left">Key Metrics</Divider>
                          <Row gutter={[16, 16]}>
                            {essentialMetrics.map((metric) => (
                              <Col xs={24} md={12} lg={8} key={metric.key}>
                                <Card size="small" bodyStyle={{ padding: 16 }}>
                                  <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                    <Space align="center" style={{ justifyContent: 'space-between' }}>
                                      <Text strong>
                                        {metric.icon}
                                        {metric.icon ? <span style={{ marginLeft: 6 }}>{metric.title}</span> : metric.title}
                                      </Text>
                                      {metric.key === 'success' && (
                                        <FundProjectionScreenOutlined style={{ color: '#1890ff' }} />
                                      )}
                                    </Space>
                                    <Statistic
                                      title={null}
                                      value={metric.value}
                                      prefix={metric.prefix}
                                      suffix={metric.suffix}
                                      precision={metric.precision}
                                      valueStyle={{ fontSize: 20 }}
                                    />
                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                      {metric.description}
                                    </Text>
                                  </Space>
                                </Card>
                              </Col>
                            ))}
                          </Row>
                        </>
                      )}

                      {currentRun.status === 'running' && (
                        <Alert
                          message="Crew is executing"
                          description="Your crew is working on the task. This may take several minutes depending on complexity. The status will update automatically."
                          type="info"
                          showIcon
                          icon={<Spin />}
                        />
                      )}

                      {currentRun.status === 'completed' && currentRun.output && (
                        <>
                          <Alert
                            message="Execution completed successfully"
                            description="Review the generated output below or navigate to Results for historical runs."
                            type="success"
                            showIcon
                            style={{ marginBottom: 12 }}
                          />
                          <Card size="small" bodyStyle={{ padding: 16 }}>
                            <Space direction="vertical" style={{ width: '100%' }}>
                              <Space align="center" style={{ justifyContent: 'space-between', width: '100%' }}>
                                <Text strong>Execution Result</Text>
                                <Button size="small" icon={<CopyOutlined />} onClick={handleCopyOutput}>
                                  Copy Output
                                </Button>
                              </Space>
                              <div
                                style={{
                                  maxHeight: 280,
                                  overflow: 'auto',
                                  backgroundColor: '#0f172a',
                                  color: '#e2e8f0',
                                  padding: 12,
                                  borderRadius: 6,
                                  fontFamily: 'SFMono-Regular, Consolas, Menlo, monospace',
                                  fontSize: 12,
                                }}
                              >
                                <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{currentRun.output}</pre>
                              </div>
                            </Space>
                          </Card>
                        </>
                      )}

                      {currentRun.status === 'failed' && currentRun.error && (
                        <Alert
                          message="Execution failed"
                          description={currentRun.error}
                          type="error"
                          showIcon
                        />
                      )}

                      {/* Line item tracking removed to simplify run details */}
                    </Space>
                  ),
                },
                {
                  key: 'timeline',
                  label: 'Execution Timeline',
                  children: (
                    <div>
                      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                        <Text type="secondary">
                          Step-by-step visibility helps you understand what each agent is doing in real-time.
                        </Text>
                        <Timeline items={timelineItems} pending={currentRun.status === 'running'} />
                      </Space>
                    </div>
                  ),
                },
                {
                  key: 'logs',
                  label: 'Activity Log',
                  children: (
                    <div>
                      <Text type="secondary">Live feed of agent actions and status updates</Text>
                      <div style={{
                        marginTop: 12,
                        maxHeight: 280,
                        overflow: 'auto',
                        backgroundColor: '#0f172a',
                        color: '#e2e8f0',
                        padding: 12,
                        borderRadius: 6,
                        fontFamily: 'SFMono-Regular, Consolas, Menlo, monospace',
                        fontSize: 12,
                      }}>
                        {logs.length > 0 ? (
                          <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                            {logs.join('\n\n')}
                          </pre>
                        ) : (
                          <Text type="secondary" style={{ color: '#94a3b8' }}>
                            Waiting for log data...
                          </Text>
                        )}
                      </div>
                    </div>
                  ),
                },
              ]}
            />
          </Card>
        )}
        </Col>
      </Row>

      {/* Edit Modal for Lists (Locations & Services) */}
      <Modal
        title={
          editingField === 'service_locations'
            ? 'Edit Service Locations'
            : 'Edit Services Offered'
        }
        open={editModalVisible}
        onCancel={() => {
          setEditModalVisible(false);
          setEditingField(null);
        }}
        width={700}
        footer={[
          <Button key="cancel" onClick={() => setEditModalVisible(false)}>
            Cancel
          </Button>,
          <Button
            key="save"
            type="primary"
            icon={<SaveOutlined />}
            onClick={() => {
              const values = configForm.getFieldsValue();
              if (editingField === 'service_locations') {
                setPrefillData(prev => prev ? {
                  ...prev,
                  brand_bible: {
                    ...prev.brand_bible,
                    service_locations: values.service_locations
                  },
                  tenant_settings: {
                    ...prev.tenant_settings,
                    disallowed_locations: Array.isArray(values.disallowed_locations)
                      ? values.disallowed_locations.join(', ')
                      : values.disallowed_locations,
                  }
                } : null);
              } else if (editingField === 'services_offered') {
                setPrefillData(prev => prev ? {
                  ...prev,
                  brand_bible: {
                    ...prev.brand_bible,
                    services_offered: values.services_offered
                  }
                } : null);
              }
              setEditModalVisible(false);
              setEditingField(null);
              message.success('Changes saved successfully');
            }}
          >
            Save Changes
          </Button>
        ]}
      >
        <Form form={configForm} layout="vertical">
          {editingField === 'service_locations' ? (
            <div>
              <Alert
                type="info"
                message="Manage Service Locations"
                description="Add or remove locations where your services are offered. Each location will generate a dedicated landing page."
                style={{ marginBottom: 16 }}
                showIcon
              />
              <Space size="small" style={{ marginBottom: 8 }}>
                <Button 
                  size="small" 
                  onClick={() => configForm.setFieldsValue({ service_locations: locationSuggestions })}
                >
                  Select All
                </Button>
                <Button size="small" onClick={() => configForm.setFieldsValue({ service_locations: [] })}>
                  Unselect All
                </Button>
              </Space>
              <Form.Item
                label="Service Locations (one per line)"
                name="service_locations"
                help="Format: City, State (e.g., St Louis, MO)"
              >
                <Select
                  mode="tags"
                  style={{ width: '100%' }}
                  placeholder="Type location and press Enter"
                  tokenSeparators={[',']}
                  notFoundContent={null}
                  value={configForm.getFieldValue('service_locations') || []}
                  onChange={(value) => configForm.setFieldsValue({ service_locations: value })}
                  options={locationSuggestions.map(loc => ({ label: loc, value: loc }))}
                  filterOption={(input, option) =>
                    (option?.label as string ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  showSearch
                />
              </Form.Item>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Total: {configForm.getFieldValue('service_locations')?.length || 0} locations
              </Text>
            </div>
          ) : editingField === 'services_offered' ? (
            <div>
              <Alert
                type="info"
                message="Manage Services Offered"
                description="Add or remove services your business provides. These will be highlighted in your content."
                style={{ marginBottom: 16 }}
                showIcon
              />
              <Space size="small" style={{ marginBottom: 8 }}>
                <Button 
                  size="small" 
                  onClick={() => configForm.setFieldsValue({ services_offered: serviceSuggestions })}
                >
                  Select All
                </Button>
                <Button size="small" onClick={() => configForm.setFieldsValue({ services_offered: [] })}>
                  Unselect All
                </Button>
              </Space>
              <Form.Item
                label="Services Offered (one per line)"
                name="services_offered"
                help="Describe each service clearly"
              >
                <Select
                  mode="tags"
                  style={{ width: '100%' }}
                  placeholder="Type service and press Enter"
                  tokenSeparators={[',']}
                  notFoundContent={null}
                  value={configForm.getFieldValue('services_offered') || []}
                  onChange={(value) => configForm.setFieldsValue({ services_offered: value })}
                  options={serviceSuggestions.map(service => ({ label: service, value: service }))}
                  filterOption={(input, option) =>
                    (option?.label as string ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  showSearch
                />
              </Form.Item>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Total: {configForm.getFieldValue('services_offered')?.length || 0} services
              </Text>
            </div>
          ) : null}
        </Form>
      </Modal>
    </div>
  );
};

export default RunPage;
